/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

export const MCP_CLIENT_IDS = [
  'claude-code',
  'claude-desktop',
  'cursor',
  'opencode',
  'codex',
] as const;
export type McpClientId = (typeof MCP_CLIENT_IDS)[number];

export const CLIENT_LABELS: Record<McpClientId, string> = {
  'claude-code': 'Claude Code',
  'claude-desktop': 'Claude Desktop',
  cursor: 'Cursor',
  opencode: 'OpenCode',
  codex: 'Codex',
};

export type EditorTab = {
  id: string;
  label: string;
  body: string;
  /** Copyable snippet (shell command or config JSON) — must be paste-safe
   * as a unit: never pair an interactive command with a second line it
   * would swallow from stdin. */
  snippet: string;
  /** Terminal chrome title — names where the snippet lives. */
  chromeTitle: string;
  /** Leading prompt glyph; only shell tabs earn a `$`. */
  prompt: string;
  /** Faux response printed after a copy — verifiable, rot-proof facts only
   * (no counts that drift with plugin releases). */
  copiedLine: string;
  /** Note rendered under the terminal — the one caveat a reader needs before
   * the snippet behaves as advertised. */
  note?: string;
  /** What must already be on the machine for a local server to start. */
  requires?: string;
  /** Primary action — a deeplink the editor itself handles. Only for editors
   * that register one; the `hint` must say what the click really does. */
  connect?: { label: string; href: string; hint: string };
  links: { label: string; href: string }[];
  /** Mutually exclusive setups WITHIN one client, rendered as a toggle. Only a
   * client whose two paths differ in what the user ends up with earns them; a
   * toggle over cosmetic variants would just add a decision. */
  variants?: EditorVariant[];
};

/** A variant replaces named fields of its tab while selected. Overrides are
 * explicit rather than a spread of the whole tab so `id`/`label` (the toggle's
 * own identity) can never collide with the tab's. */
export type EditorVariant = {
  id: string;
  label: string;
  overrides: Partial<Omit<EditorTab, 'id' | 'label' | 'variants'>>;
};

/** A top-level tab: one per vendor, holding one or more of its clients. A
 * vendor with several clients shows them as a segmented picker above the setup
 * toggle, so the strip stays one tab per company rather than one per product. */
export type EditorGroup = {
  id: string;
  label: string;
  clients: EditorTab[];
};

/** A plugin that bundles the connector with skills/agents; installable from a
 * marketplace by Claude Code and Codex. */
export type McpPlugin = { id: string; source: string; guideUrl?: string };

/** Replaces generated fields of one client; `variants` merge into the named
 * variant's overrides. For servers verified in depth, where the generic copy
 * would say less than we know. */
export type ClientOverride = Partial<Omit<EditorTab, 'id' | 'label' | 'variants'>> & {
  variants?: Record<string, EditorVariant['overrides']>;
};

export type McpEnvVar = { name: string; description?: string; required?: boolean; value?: string };

/** Remote: the client connects to a hosted streamable-http endpoint. Local
 * (PLAT-2691): the client starts `command args…` itself and talks stdio. */
export type McpTransport =
  | { kind: 'remote'; url: string }
  | { kind: 'local'; command: string; args: string[]; env?: McpEnvVar[]; requires?: string };

export type McpServerDescriptor = {
  /** Install name: the key in every command and config snippet. */
  name: string;
  title: string;
  transport: McpTransport;
  clients: McpClientId[];
  plugin?: McpPlugin;
  docLink?: { label: string; href: string };
  overrides?: Partial<Record<McpClientId, ClientOverride>>;
};
